import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/api/client';
import { AppLayout } from '@/components/Layout/AppLayout';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

// ─── Types ───────────────────────────────────────────────────────────────────

interface QuizQuestion {
  id: string;
  type: string;
  question: string;
  correct: string;
  options: string[];
  hint?: string;
}

type GameState = 'start' | 'playing' | 'answered' | 'finished';

const OPTION_LABELS = ['A', 'B', 'C', 'D'];
const TIMER_SECONDS = 20;

// ─── API ─────────────────────────────────────────────────────────────────────

async function fetchQuiz(): Promise<QuizQuestion[]> {
  const { data } = await apiClient.get('/quiz/generate');
  return data;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Render question text: split on ** and alternate normal/bold spans */
function QuestionText({ text }: { text: string }) {
  const parts = text.split('**');
  return (
    <span>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <strong key={i} className="text-blue-700">
            {part}
          </strong>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </span>
  );
}

function StarRating({ score, total }: { score: number; total: number }) {
  const pct = total === 0 ? 0 : score / total;
  const stars = pct >= 0.9 ? 3 : pct >= 0.7 ? 2 : pct >= 0.4 ? 1 : 0;

  return (
    <div className="flex justify-center gap-2">
      {[0, 1, 2].map((i) => (
        <svg
          key={i}
          className={`h-12 w-12 transition-all duration-500 ${
            i < stars ? 'scale-110 text-yellow-400 drop-shadow-md' : 'text-slate-200'
          }`}
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </div>
  );
}

function PerformanceMessage({ score, total }: { score: number; total: number }) {
  const pct = total === 0 ? 0 : score / total;
  if (pct === 1)
    return <p className="text-2xl font-bold text-yellow-600">🏆 Perfekt! Du bist ein echter Familien-Experte!</p>;
  if (pct >= 0.7)
    return <p className="text-2xl font-bold text-green-600">🌟 Ausgezeichnet! Du kennst deinen Stammbaum sehr gut!</p>;
  if (pct >= 0.5)
    return <p className="text-2xl font-bold text-blue-600">👍 Gut gemacht! Da ist aber noch etwas Luft nach oben.</p>;
  return <p className="text-2xl font-bold text-slate-600">📚 Schau doch mal öfter in den Stammbaum!</p>;
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function QuizPage() {
  const navigate = useNavigate();

  // ── Game state ──
  const [gameState, setGameState] = useState<GameState>('start');
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(TIMER_SECONDS);
  const [quizKey, setQuizKey] = useState(0); // bumped to force refetch on replay

  // ── Data fetching ──
  const { data: questions = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['quiz', quizKey],
    queryFn: fetchQuiz,
    enabled: true,
    staleTime: 0,
  });

  const currentQuestion = questions[currentIdx] ?? null;
  const totalQuestions = questions.length;

  // ── Timer countdown ──
  useEffect(() => {
    if (gameState !== 'playing') return;
    if (timeLeft <= 0) {
      // Time's up — mark as answered with no selection (wrong)
      setGameState('answered');
      return;
    }
    const id = setInterval(() => setTimeLeft((t) => t - 1), 1000);
    return () => clearInterval(id);
  }, [gameState, timeLeft]);

  // ── Actions ──

  const startQuiz = useCallback(() => {
    setCurrentIdx(0);
    setScore(0);
    setSelected(null);
    setTimeLeft(TIMER_SECONDS);
    setGameState('playing');
  }, []);

  const handleSelect = useCallback(
    (option: string) => {
      if (gameState !== 'playing') return;
      setSelected(option);
      setGameState('answered');
      if (option === currentQuestion?.correct) {
        setScore((s) => s + 1);
      }
    },
    [gameState, currentQuestion],
  );

  const handleNext = useCallback(() => {
    const nextIdx = currentIdx + 1;
    if (nextIdx >= totalQuestions) {
      setGameState('finished');
    } else {
      setCurrentIdx(nextIdx);
      setSelected(null);
      setTimeLeft(TIMER_SECONDS);
      setGameState('playing');
    }
  }, [currentIdx, totalQuestions]);

  const handleReplay = useCallback(() => {
    setQuizKey((k) => k + 1);
    setCurrentIdx(0);
    setScore(0);
    setSelected(null);
    setTimeLeft(TIMER_SECONDS);
    setGameState('start');
    refetch();
  }, [refetch]);

  // ── Timer bar colour ──
  const timerPct = (timeLeft / TIMER_SECONDS) * 100;
  const timerColor =
    timerPct > 60 ? 'bg-green-500' : timerPct > 30 ? 'bg-yellow-400' : 'bg-red-500';

  // ─────────────────────────────────────────────────────────────────────────────
  // Render helpers
  // ─────────────────────────────────────────────────────────────────────────────

  function renderOptionButton(option: string, idx: number) {
    const label = OPTION_LABELS[idx] ?? String(idx + 1);
    const isCorrect = option === currentQuestion?.correct;
    const isSelected = option === selected;
    const isAnswered = gameState === 'answered';

    let cls =
      'relative flex w-full items-center gap-3 rounded-xl border-2 px-5 py-4 text-left text-base font-medium transition-all duration-200 focus:outline-none';

    if (!isAnswered) {
      cls += ' border-slate-200 bg-white text-slate-800 hover:border-blue-400 hover:bg-blue-50 hover:shadow-md active:scale-[0.98] cursor-pointer';
    } else if (isCorrect) {
      cls += ' border-green-500 bg-green-50 text-green-800 shadow-md';
    } else if (isSelected) {
      cls += ' border-red-500 bg-red-50 text-red-800';
    } else {
      cls += ' border-slate-100 bg-slate-50 text-slate-400 opacity-60';
    }

    return (
      <button
        key={option}
        className={cls}
        onClick={() => handleSelect(option)}
        disabled={isAnswered}
      >
        {/* Label badge */}
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
            !isAnswered
              ? 'bg-slate-100 text-slate-600'
              : isCorrect
              ? 'bg-green-500 text-white'
              : isSelected
              ? 'bg-red-500 text-white'
              : 'bg-slate-200 text-slate-400'
          }`}
        >
          {label}
        </span>

        <span className="flex-1">{option}</span>

        {/* Result icon */}
        {isAnswered && isCorrect && (
          <svg className="h-5 w-5 shrink-0 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
          </svg>
        )}
        {isAnswered && isSelected && !isCorrect && (
          <svg className="h-5 w-5 shrink-0 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        )}
      </button>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // START SCREEN
  // ─────────────────────────────────────────────────────────────────────────────

  if (gameState === 'start') {
    return (
      <AppLayout>
        <div className="flex min-h-full items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
          <div className="w-full max-w-lg">
            {/* Hero card */}
            <div className="overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-slate-200">
              {/* Colourful header strip */}
              <div className="bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 px-8 py-10 text-center">
                <div className="mb-4 text-6xl">🧬</div>
                <h1 className="text-4xl font-extrabold tracking-tight text-white drop-shadow">Familien-Quiz</h1>
                <p className="mt-2 text-lg font-medium text-blue-100">Wie gut kennst du deinen Stammbaum?</p>
              </div>

              {/* Body */}
              <div className="px-8 py-8">
                <div className="mb-6 space-y-3 text-sm text-slate-600">
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-600">8</span>
                    <span>Bis zu <strong>8 Fragen</strong> aus deinen echten Familiendaten</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-600">⏱</span>
                    <span><strong>20 Sekunden</strong> pro Frage — denk schnell!</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-purple-100 text-xs font-bold text-purple-600">★</span>
                    <span>Verdiene bis zu <strong>3 Sterne</strong> für deine Leistung</span>
                  </div>
                </div>

                {isLoading ? (
                  <div className="py-4">
                    <LoadingSpinner message="Fragen werden geladen..." />
                  </div>
                ) : isError ? (
                  <div className="rounded-xl bg-red-50 p-4 text-center text-sm text-red-600">
                    Fehler beim Laden der Fragen. Bitte versuche es erneut.
                  </div>
                ) : questions.length === 0 ? (
                  <div className="rounded-xl bg-amber-50 p-4 text-center text-sm text-amber-700">
                    Zu wenig Familiendaten für ein Quiz. Bitte füge mehr Personen hinzu.
                  </div>
                ) : (
                  <button
                    onClick={startQuiz}
                    className="w-full rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-600 px-6 py-4 text-lg font-bold text-white shadow-lg transition-all hover:from-blue-600 hover:to-indigo-700 hover:shadow-xl active:scale-[0.98]"
                  >
                    Quiz starten →
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // FINISHED SCREEN
  // ─────────────────────────────────────────────────────────────────────────────

  if (gameState === 'finished') {
    const pct = totalQuestions === 0 ? 0 : Math.round((score / totalQuestions) * 100);

    return (
      <AppLayout>
        <div className="flex min-h-full items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
          <div className="w-full max-w-lg">
            <div className="overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-slate-200">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 px-8 py-8 text-center">
                <div className="mb-3 text-5xl">🎉</div>
                <h2 className="text-3xl font-extrabold text-white">Quiz beendet!</h2>
              </div>

              {/* Score */}
              <div className="px-8 py-8 text-center">
                <div className="mb-6">
                  <p className="text-6xl font-black text-slate-900">
                    {score}
                    <span className="text-3xl font-semibold text-slate-400"> / {totalQuestions}</span>
                  </p>
                  <p className="mt-1 text-lg font-semibold text-slate-500">{pct}% richtig</p>
                </div>

                <div className="mb-6">
                  <StarRating score={score} total={totalQuestions} />
                </div>

                <div className="mb-8">
                  <PerformanceMessage score={score} total={totalQuestions} />
                </div>

                {/* Score bar */}
                <div className="mb-8 overflow-hidden rounded-full bg-slate-100 h-3">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-1000"
                    style={{ width: `${pct}%` }}
                  />
                </div>

                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    onClick={handleReplay}
                    className="flex-1 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 px-5 py-3 font-bold text-white shadow hover:from-blue-600 hover:to-indigo-700 active:scale-[0.98] transition-all"
                  >
                    🔄 Nochmal spielen
                  </button>
                  <button
                    onClick={() => navigate('/')}
                    className="flex-1 rounded-xl border-2 border-slate-200 bg-white px-5 py-3 font-bold text-slate-700 hover:bg-slate-50 active:scale-[0.98] transition-all"
                  >
                    🌳 Stammbaum erkunden
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // PLAYING / ANSWERED SCREEN
  // ─────────────────────────────────────────────────────────────────────────────

  if (!currentQuestion) return null;

  const isAnswered = gameState === 'answered';

  return (
    <AppLayout>
      <div className="min-h-full bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4 sm:p-6">
        <div className="mx-auto max-w-2xl">
          {/* ── Top bar: progress + score ── */}
          <div className="mb-4 flex items-center justify-between gap-4">
            <div className="flex-1">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Frage {currentIdx + 1} von {totalQuestions}
              </p>
              {/* Question progress dots */}
              <div className="flex gap-1">
                {questions.map((_, i) => (
                  <div
                    key={i}
                    className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                      i < currentIdx
                        ? 'bg-indigo-400'
                        : i === currentIdx
                        ? 'bg-blue-500'
                        : 'bg-slate-200'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Score badge */}
            <div className="flex shrink-0 items-center gap-1.5 rounded-full bg-indigo-600 px-4 py-1.5 text-sm font-bold text-white shadow">
              <svg className="h-4 w-4 text-yellow-300" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              {score} richtig
            </div>
          </div>

          {/* ── Timer bar ── */}
          <div className="mb-5 flex items-center gap-3">
            <div className="flex-1 overflow-hidden rounded-full bg-slate-200 h-2.5">
              <div
                className={`h-full rounded-full transition-all duration-1000 ${timerColor}`}
                style={{ width: `${timerPct}%` }}
              />
            </div>
            <span
              className={`w-8 text-right text-sm font-bold tabular-nums ${
                timeLeft <= 5 ? 'animate-pulse text-red-500' : 'text-slate-600'
              }`}
            >
              {timeLeft}s
            </span>
          </div>

          {/* ── Question card ── */}
          <div className="mb-5 rounded-2xl bg-white p-6 shadow-xl ring-1 ring-slate-100">
            {/* Question type badge */}
            <span className="mb-4 inline-block rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-600">
              {currentQuestion.type === 'birth_place' && 'Geburtsort'}
              {currentQuestion.type === 'birth_year' && 'Geburtsjahr'}
              {currentQuestion.type === 'death_year' && 'Sterbejahr'}
              {currentQuestion.type === 'marriage_partner' && 'Ehepartner'}
              {currentQuestion.type === 'child_count' && 'Kinderzahl'}
            </span>

            <h2 className="text-xl font-bold leading-snug text-slate-900 sm:text-2xl">
              <QuestionText text={currentQuestion.question} />
            </h2>

            {currentQuestion.hint && (
              <p className="mt-3 text-sm italic text-slate-400">
                💡 Hinweis: {currentQuestion.hint}
              </p>
            )}
          </div>

          {/* ── Answer buttons ── */}
          <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {currentQuestion.options.map((option, idx) => renderOptionButton(option, idx))}
          </div>

          {/* ── Feedback banner (after answering) ── */}
          {isAnswered && (
            <div
              className={`mb-5 flex items-center gap-3 rounded-xl px-5 py-4 text-base font-semibold ${
                selected === currentQuestion.correct
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-50 text-red-800'
              }`}
            >
              {selected === currentQuestion.correct ? (
                <>
                  <span className="text-2xl">🎉</span>
                  <span>Richtig! Sehr gut!</span>
                </>
              ) : selected === null ? (
                <>
                  <span className="text-2xl">⏰</span>
                  <span>
                    Zeit abgelaufen! Richtige Antwort:{' '}
                    <strong>{currentQuestion.correct}</strong>
                  </span>
                </>
              ) : (
                <>
                  <span className="text-2xl">❌</span>
                  <span>
                    Leider falsch. Richtige Antwort:{' '}
                    <strong>{currentQuestion.correct}</strong>
                  </span>
                </>
              )}
            </div>
          )}

          {/* ── Next button ── */}
          {isAnswered && (
            <button
              onClick={handleNext}
              className="w-full rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-4 text-lg font-bold text-white shadow-lg transition-all hover:from-indigo-600 hover:to-purple-700 hover:shadow-xl active:scale-[0.98]"
            >
              {currentIdx + 1 >= totalQuestions ? 'Ergebnis anzeigen 🏁' : 'Weiter →'}
            </button>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
